import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Field, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ListChecks, HelpCircle, Loader2 } from "lucide-react";
import { AceExperts } from "@/components/AceExperts";

const FIELD_TYPES = [
  ["text", "Short answer"],
  ["textarea", "Long answer"],
  ["select", "Pick from a list"],
  ["date", "Date"],
  ["number", "Number"],
];

export default function Lists() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("event_types");
  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/option-lists", { params: { include_inactive: true } }).then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cc-blue" /></div>;

  const keys = Object.keys(data.meta);
  const meta = data.meta[active];
  const options = data.lists[active] || [];

  const add = async () => {
    const label = newLabel.trim();
    if (!label) return toast.error("Type a name first");
    try { await api.post(`/option-lists/${active}`, { label }); setNewLabel(""); toast.success("Option added"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (o) => {
    try { await api.patch(`/option-lists/${active}/${o.id}`, { active: !o.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (o) => {
    try {
      const { data: r } = await api.delete(`/option-lists/${active}/${o.id}`);
      toast.success(r.retired ? "Retired — kept for existing records" : "Removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="lists-page">
      <PageHeader title="Lists & Options" subtitle="Add, rename or retire the choices behind the app's dropdowns" />

      <AceExperts />

      <div className="flex flex-wrap gap-2" data-testid="list-tabs">
        {keys.map((k) => (
          <button key={k} onClick={() => setActive(k)}
            className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${active === k ? "bg-cc-blue text-white border-cc-blue" : "bg-white text-zinc-600 hover:border-cc-blue"}`}
            data-testid={`list-tab-${k}`}>
            {data.meta[k].label}
          </button>
        ))}
      </div>

      <Card title={meta.label}>
        <p className="text-sm text-zinc-500 mb-4">{meta.hint}{meta.supports_fields ? " Options in this list can also ask follow-up questions." : ""}</p>

        <div className="flex gap-2 mb-5 max-w-lg">
          <Input placeholder={`Add a new ${meta.label.replace(/s$/, "").toLowerCase()}`} value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="new-option-label" />
          <Button onClick={add} className="bg-cc-blue text-white shrink-0" data-testid="add-option-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>

        {options.length === 0 ? <EmptyState text="No options yet — add the first one above." /> : (
          <div className="space-y-2" data-testid="option-rows">
            {options.map((o) => (
              <div key={o.id} className={`flex flex-wrap items-center gap-3 border rounded-md px-3.5 py-2.5 ${o.active ? "" : "bg-zinc-50 opacity-70"}`} data-testid={`option-row-${o.id}`}>
                <ListChecks className="w-4 h-4 text-cc-blue shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-zinc-800">{o.label}</div>
                  {(o.fields || []).length > 0 && (
                    <div className="text-xs text-zinc-400 flex items-center gap-1"><HelpCircle className="w-3 h-3" />{o.fields.length} follow-up question{o.fields.length > 1 ? "s" : ""}: {o.fields.map((f) => f.label).join(", ")}</div>
                  )}
                  {!o.active && <div className="text-xs text-amber-600">Retired — hidden from new forms</div>}
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                    <Switch checked={!!o.active} onCheckedChange={() => toggle(o)} data-testid={`option-active-${o.id}`} />
                    {o.active ? "In use" : "Off"}
                  </div>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditing({ ...o, fields: o.fields || [] })} data-testid={`option-edit-${o.id}`}>
                    <Pencil className="w-3 h-3 mr-1" />Edit
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs text-red-600" onClick={() => remove(o)} data-testid={`option-delete-${o.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editing && (
        <EditOption listKey={active} supportsFields={meta.supports_fields} option={editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function EditOption({ listKey, supportsFields, option, onClose, onSaved }) {
  const [label, setLabel] = useState(option.label);
  const [fields, setFields] = useState(option.fields || []);
  const [saving, setSaving] = useState(false);

  const setField = (i, patch) => setFields(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const addField = () => setFields([...fields, { label: "", type: "text", options: [], required: false }]);
  const removeField = (i) => setFields(fields.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!label.trim()) return toast.error("A name is required");
    setSaving(true);
    try {
      const body = { label: label.trim() };
      if (supportsFields) body.fields = fields.filter((f) => (f.label || "").trim());
      await api.patch(`/option-lists/${listKey}/${option.id}`, body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto cc-scroll" data-testid="edit-option-dialog">
        <DialogHeader>
          <DialogTitle>Edit option</DialogTitle>
          <DialogDescription>Rename it{supportsFields ? ", and add follow-up questions that appear when someone picks it." : "."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Name"><Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="edit-option-label" /></Field>

          {supportsFields && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-zinc-800">Follow-up questions</p>
                <Button size="sm" variant="outline" onClick={addField} data-testid="add-field-btn"><Plus className="w-3 h-3 mr-1" />Add question</Button>
              </div>
              {fields.length === 0 && <p className="text-xs text-zinc-400">None — picking this option won't ask anything extra.</p>}
              {fields.map((f, i) => (
                <div key={i} className="border rounded-md p-3 space-y-2" data-testid={`field-row-${i}`}>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Field label="Question"><Input value={f.label} onChange={(e) => setField(i, { label: e.target.value })} placeholder="e.g. Which restaurant?" data-testid={`field-label-${i}`} /></Field>
                    <Field label="Answer type">
                      <Select value={f.type} onValueChange={(v) => setField(i, { type: v })}>
                        <SelectTrigger data-testid={`field-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{FIELD_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                  </div>
                  {f.type === "select" && (
                    <Field label="Choices (comma separated)">
                      <Input value={(f.options || []).join(", ")} onChange={(e) => setField(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
                        placeholder="Chipotle, Panera, Local deli" data-testid={`field-options-${i}`} />
                    </Field>
                  )}
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs text-zinc-600">
                      <Switch checked={!!f.required} onCheckedChange={(v) => setField(i, { required: v })} data-testid={`field-required-${i}`} />
                      Required
                    </label>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => removeField(i)} data-testid={`field-remove-${i}`}>Remove</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-cc-blue text-white" data-testid="save-option-btn">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
